import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthorizationStore } from '@/stores/authorization';
import { Activity, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MdAdminPanelSettings, MdLocalHospital } from 'react-icons/md';
import { FaBuilding } from 'react-icons/fa6';
import {
  canUseMultiEstablishmentContext,
  useAuthStore,
} from '@/stores/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LogoGeoScalaIconLight from '@/assets/images/logo-icone-light.svg';

const MODULE_ICONS: Record<string, React.ReactNode> = {
  administrative: <MdAdminPanelSettings className="h-7 w-7" />,
  establishment: <MdLocalHospital className="h-7 w-7" />,
  medical_company: <FaBuilding className="h-7 w-7" />,
  medical_management: <Activity className="h-7 w-7" />,
};

const MODULE_LABEL_KEYS: Record<string, string> = {
  administrative: 'module_selector.modules.administrative',
  establishment: 'module_selector.modules.establishment',
  medical_company: 'module_selector.modules.medical_company',
  medical_management: 'module_selector.modules.medical_management',
};

interface ModuleSelectorProps {
  collapsed?: boolean;
}

export function ModuleSelector({ collapsed = false }: ModuleSelectorProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const payload = useAuthorizationStore((s) => s.payload);
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const setSelectedModule = useAuthorizationStore((s) => s.setSelectedModule);
  const user = useAuthStore((s) => s.user);
  const requireContextSelection = useAuthStore(
    (s) => s.requireContextSelection,
  );
  const modules = payload?.modules ?? [];
  if (modules.length <= 1) {
    return (
      <div
        className={`flex w-full items-center ${
          collapsed ? 'justify-center' : 'justify-start px-2'
        }`}
        aria-label="GeoScala"
      >
        <img
          src={LogoGeoScalaIconLight}
          alt="GeoScala"
          className="h-9 w-9 object-contain brightness-0 invert"
        />
      </div>
    );
  }

  const currentModule =
    modules.find((m) => m.key === selectedModuleKey) ?? modules[0];

  const handleSwitch = (moduleKey: string, defaultRoute: string) => {
    setSelectedModule(moduleKey);

    const canUseContext = canUseMultiEstablishmentContext(user);
    if (
      ['establishment', 'medical_company'].includes(moduleKey) &&
      canUseContext
    ) {
      requireContextSelection(defaultRoute);
      return;
    }

    navigate(defaultRoute);
  };

  const getModuleLabel = (moduleKey: string) => {
    const translationKey = MODULE_LABEL_KEYS[moduleKey];
    return translationKey ? t(translationKey) : moduleKey;
  };

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`w-full rounded-2xl transition ${
            menuOpen
              ? 'border border-sidebar-border bg-sidebar-accent/30 hover:bg-sidebar-accent/50'
              : 'border border-transparent bg-transparent hover:bg-sidebar-accent/20'
          } ${
            collapsed
              ? 'flex justify-center px-1 py-2.5'
              : 'flex items-center gap-2 px-2 pr-1 py-2.5 text-left'
          }`}
          title={getModuleLabel(currentModule.key)}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-[.3rem] bg-primary-light text-sidebar-foreground">
            {MODULE_ICONS[currentModule.key]}
          </div>

          {!collapsed && (
            <>
              <div className="min-w-0 flex-1 pr-1">
                <p className="overflow-hidden whitespace-nowrap text-clip text-[15px] font-semibold leading-tight text-sidebar-foreground">
                  {getModuleLabel(currentModule.key)}
                </p>
                <p className="text-[11px] text-sidebar-foreground/70">
                  {t('module_selector.active_module')}
                </p>
              </div>
              <ChevronsUpDown className="h-3 w-3 shrink-0 text-sidebar-foreground/70" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[14.4rem] rounded-2xl border-border/70 p-2"
      >
        {modules.map((mod) => (
          <DropdownMenuItem
            key={mod.key}
            onClick={() => handleSwitch(mod.key, mod.default_route)}
            className="cursor-pointer gap-2 rounded-xl px-1 py-1.5 text-[14px] dark:hover:text-white dark:focus:text-white"
          >
            <span className="dark:text-white [&_svg]:h-5 [&_svg]:w-5">
              {MODULE_ICONS[mod.key]}
            </span>
            <span className="truncate">
              {getModuleLabel(mod.key)}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
